/**
 * Shared network vocabulary for every surface that draws social marks: the
 * profile hero's linked {@link SocialLinks} chips and the results card's
 * display-only marks (TRU-35).
 *
 * One home for the three facts both surfaces need — which `network` strings are
 * real, what each is called, and which glyph draws it — so a new network is
 * added once rather than in two places that can silently drift apart.
 */

import { Icon } from "@to/ui";
import type { AdSocialNetwork } from "@to/db";
import { BRAND_ICONS } from "./brand-icons";

/**
 * Accessible label per network.
 *
 * `Record<AdSocialNetwork, string>` makes this exhaustive BY TYPE: adding a
 * network to the schema's `AD_SOCIAL_NETWORKS` fails the typecheck here until
 * its label lands, so the map below is a safe runtime stand-in for that list —
 * and this module stays free of value imports from `@to/db`, which would drag
 * the postgres client into the client bundle (the card is a client component).
 */
export const NETWORK_LABELS: Record<AdSocialNetwork, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  x_twitter: "X (Twitter)",
  zillow: "Zillow",
  realtor_com: "Realtor.com",
  website: "Website",
};

/** Membership set for the 9 valid networks — drives the unknown-value drop. */
const VALID_NETWORKS = new Set<string>(Object.keys(NETWORK_LABELS));

/**
 * Narrows a stored `network` string to a known network. Rows carrying anything
 * else are dropped silently by the callers — never thrown (garbage in the
 * column must not take a page down).
 */
export function isSocialNetwork(network: string): network is AdSocialNetwork {
  return VALID_NETWORKS.has(network);
}

/** The mark for one network: a brand SVG, or the `language` Icon for `website`. */
export function networkMark(network: AdSocialNetwork, size = 20) {
  if (network === "website") {
    // `website` has no brand mark — render the Material Symbols `language` glyph.
    return <Icon name="language" size={size} />;
  }
  const BrandIcon = BRAND_ICONS[network];
  return <BrandIcon size={size} />;
}
